import { getAuthoredOverview, renderAuthoredNarrativeBundle } from './authoredNarrativeRender';
import { shouldPublishAuthoredMethodology } from './authoredNarrativeLoader';
import type { IncomePovertyFacts } from './peopleIncomePovertyNarrativeFacts';

const CHART_ID = 'income_and_poverty_by_race';

export type IncomePovertyViewId =
  | 'income-trends'
  | 'poverty-trends'
  | 'income-bar'
  | 'poverty-bar';

function narrativeViewId(view: IncomePovertyViewId): string {
  return view;
}

export function buildIncomePovertyTokens(
  locationName: string,
  facts: IncomePovertyFacts | null,
  view: IncomePovertyViewId
): Record<string, string> {
  const latestYear = facts?.latestYear != null ? String(facts.latestYear) : '—';
  const lowestIncome =
    facts?.lowestIncome != null ? `$${Math.round(facts.lowestIncome).toLocaleString()}` : '—';
  const highestIncome =
    facts?.highestIncome != null ? `$${Math.round(facts.highestIncome).toLocaleString()}` : '—';
  const lowestPovertyRate =
    facts?.lowestPovertyRate != null ? facts.lowestPovertyRate.toFixed(1) : '—';
  const highestPovertyRate =
    facts?.highestPovertyRate != null ? facts.highestPovertyRate.toFixed(1) : '—';

  const gapSentence =
    view === 'income-bar' || view === 'income-trends'
      ? facts?.incomeRatio != null
        ? ` Median income spanned a ${facts.incomeRatio.toFixed(1)}× range across displayed groups.`
        : ''
      : facts?.povertyRatio != null
        ? ` Poverty rates spanned a ${facts.povertyRatio.toFixed(1)}× range across displayed groups.`
        : '';

  return {
    locationName,
    latestYear,
    lowestIncome,
    highestIncome,
    lowestIncomeGroup: facts?.lowestIncomeGroup ?? '—',
    highestIncomeGroup: facts?.highestIncomeGroup ?? '—',
    lowestPovertyRate,
    highestPovertyRate,
    lowestPovertyGroup: facts?.lowestPovertyGroup ?? '—',
    highestPovertyGroup: facts?.highestPovertyGroup ?? '—',
    gapSentence,
    uncertaintySentence: '',
  };
}

export function buildIncomePovertyAuthoredBundle(
  locationName: string,
  facts: IncomePovertyFacts | null,
  view: IncomePovertyViewId
) {
  const tokens = buildIncomePovertyTokens(locationName, facts, view);
  const bundle = renderAuthoredNarrativeBundle(CHART_ID, narrativeViewId(view), tokens);
  if (!bundle.keyInsights && facts) {
    bundle.keyInsights = renderAuthoredNarrativeBundle(CHART_ID, 'default', tokens).keyInsights;
  }
  if (!bundle.overview && facts) {
    bundle.overview = getAuthoredOverview(CHART_ID, 'default', bundle.overview || '');
  }
  if (!shouldPublishAuthoredMethodology(bundle.authoringStatus)) {
    bundle.methodology = undefined;
  }
  return bundle;
}

export function incomePovertyOverviewDescription(view: IncomePovertyViewId, fallback: string): string {
  return getAuthoredOverview(CHART_ID, narrativeViewId(view), getAuthoredOverview(CHART_ID, 'default', fallback));
}
